/**
 * 新设备首次连接：申请配对，显示 6 位配对码；你在已经连上的设备上核对确认后自动进入（ADR-0012）。
 * 朝夕在服务器上、还没有任何已配对的设备时：在服务器上运行 zhaoxi devices approve 配对码。
 */
import { useEffect, useState } from "react";
import { errorMessage } from "@/shared/api/client";
import { me, pair } from "@/shared/api/gen/sdk.gen";
import type { PairingOut } from "@/shared/api/gen/types.gen";
import { Button, Card, ErrorNote, Spinner, TextInput } from "@/shared/ui";

function guessName(): string {
  const ua = navigator.userAgent;
  const model = /Android [\d.]+; ([^;)]+)/.exec(ua)?.[1]?.replace(/ Build.*/, "");
  if (model) return model.trim().slice(0, 40);
  if (/Android/.test(ua)) return "安卓手机";
  if (/iPhone|iPad/.test(ua)) return "iPhone";
  return "另一台设备";
}

export function PairScreen() {
  const [name, setName] = useState(guessName);
  const [state, setState] = useState<PairingOut | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void me().then((r) => r.data.status === "pending" && setState(r.data));
  }, []);

  useEffect(() => {
    if (state?.status !== "pending") return;
    const timer = setInterval(async () => {
      try {
        const r = (await me()).data;
        if (r.status === "active") location.reload();
        else setState(r);
      } catch {
        /* 网络抖动：下次再查 */
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [state?.status]);

  const request = async () => {
    setBusy(true);
    setError(null);
    try {
      setState((await pair({ body: { name } })).data);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="horizon relative grid min-h-full place-items-center bg-bg px-5 py-10">
      <Card className="flex w-full max-w-sm flex-col gap-4 p-6">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="size-7 rounded-full bg-[image:var(--horizon)] shadow-[inset_0_-10px_0_var(--action)]"
          />
          <h1 className="text-[18px] font-bold tracking-[0.15em]">朝夕</h1>
        </div>
        {state?.status === "pending" ? (
          <>
            <p className="text-[14px] leading-relaxed text-text-2">
              在已经连上朝夕的设备上（Mac
              本机，或已配对的电脑、手机）打开朝夕，核对下面的配对码并点“确认”。确认后这里会自动进入。
            </p>
            <output className="block rounded-m bg-surface-2 py-4 text-center font-mono text-[34px] font-bold tracking-[0.3em]">
              <span className="sr-only">配对码 </span>
              {state.code}
            </output>
            <div className="flex items-center gap-2 text-[13px] text-text-3">
              <Spinner /> 等待确认（15 分钟内有效）
            </div>
            <p className="text-[12.5px] leading-relaxed text-text-3">
              这是第一台设备、朝夕又装在服务器上？在服务器上运行{" "}
              <code className="rounded bg-surface-2 px-1 text-text-2">
                zhaoxi devices approve {state.code}
              </code>
            </p>
          </>
        ) : (
          <>
            <p className="text-[14px] leading-relaxed text-text-2">
              {state?.status === "denied" || state?.status === "revoked"
                ? "这台设备的配对没有通过或已被撤销，可以重新申请。"
                : "这台设备第一次连接你的朝夕。为了保护你的资料，需要先配对：这里申请，在已经连上的设备上确认。"}
            </p>
            <div className="flex flex-col gap-1 text-[13px]">
              <label htmlFor="zx-device-name" className="text-text-2">
                设备名称
              </label>
              <TextInput id="zx-device-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            {error ? <ErrorNote>{errorMessage(error)}</ErrorNote> : null}
            <Button variant="primary" busy={busy} onClick={() => void request()}>
              申请连接
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
